import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import config from '../config';

interface RoadmapItem {
  _id: string;
  type: 'todo' | 'bug';
  title: string;
  description: string;
  status: 'open' | 'in_progress' | 'completed';
  position: number;
  createdAt: string;
  completedAt: string | null;
}

interface SortableItemProps {
  item: RoadmapItem;
  onEdit: (item: RoadmapItem) => void;
  onDelete: (item: RoadmapItem) => void;
}

const SortableItem: React.FC<SortableItemProps> = ({ item, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: item._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: 'grab',
    touchAction: 'none'
  };

  return (
    <TableRow ref={setNodeRef} style={style}>
      <TableCell {...attributes} {...listeners}>
        {item.title}
      </TableCell>
      <TableCell {...attributes} {...listeners}>
        {item.description}
      </TableCell>
      <TableCell {...attributes} {...listeners}>
        {item.status}
      </TableCell>
      <TableCell {...attributes} {...listeners}>
        {new Date(item.createdAt).toLocaleDateString()}
      </TableCell>
      <TableCell {...attributes} {...listeners}>
        {item.completedAt ? new Date(item.completedAt).toLocaleDateString() : '-'}
      </TableCell>
      <TableCell>
        <IconButton onClick={(e) => {
          e.stopPropagation();
          onEdit(item);
        }} size="small">
          <EditIcon />
        </IconButton>
        <IconButton onClick={(e) => {
          e.stopPropagation();
          onDelete(item);
        }} size="small">
          <DeleteIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

const RoadmapEdit: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [items, setItems] = useState<RoadmapItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<RoadmapItem | null>(null);
  const [formData, setFormData] = useState({
    type: 'todo' as 'todo' | 'bug',
    title: '',
    description: '',
    status: 'open' as 'open' | 'in_progress' | 'completed'
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchItems = async () => {
    try {
      console.log('Fetching roadmap items');
      const token = await getAccessTokenSilently();
      const response = await axios.get(`${config.api.baseURL}/api/roadmap`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(response.data);
    } catch (error) {
      console.error('Failed to fetch items:', error);
      setError('Failed to fetch items');
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      const token = await getAccessTokenSilently();
      if (editingItem) {
        await axios.put(`${config.api.baseURL}/api/roadmap/${editingItem._id}`, {
          ...formData,
          position: editingItem.position
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${config.api.baseURL}/api/roadmap`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      fetchItems();
      setEditingItem(null);
      setFormData({ type: 'todo', title: '', description: '', status: 'open' });
      setDialogOpen(false);
    } catch (error) {
      console.error('Failed to save item:', error);
      setError('Failed to save item');
    }
  };

  const handleDelete = async (item: RoadmapItem) => {
    try {
      const token = await getAccessTokenSilently();
      await axios.delete(`${config.api.baseURL}/api/roadmap/${item._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchItems();
    } catch (error) {
      console.error('Failed to delete item:', error);
      setError('Failed to delete item');
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex(item => item._id === active.id);
    const newIndex = items.findIndex(item => item._id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const newItems = [...items];
    const [movedItem] = newItems.splice(oldIndex, 1);
    newItems.splice(newIndex, 0, movedItem);

    setItems(newItems);

    try {
      const token = await getAccessTokenSilently();
      await axios.put(`${config.api.baseURL}/api/roadmap/${movedItem._id}/reorder`, {
        newPosition: newIndex + 1
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Failed to reorder items:', error);
      setError('Failed to reorder items');
      fetchItems(); // Revert to server state on error
    }
  };

  const todoItems = items.filter(item => item.type === 'todo');
  const bugItems = items.filter(item => item.type === 'bug');

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Edit Roadmap
      </Typography>

      <Button
        variant="contained"
        color="primary"
        onClick={() => {
          setEditingItem(null);
          setFormData({ type: 'todo', title: '', description: '', status: 'open' });
          setDialogOpen(true);
        }}
        sx={{ mb: 3 }}
      >
        Add New Item
      </Button>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Typography variant="h5" gutterBottom>
        Todo List
      </Typography>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <TableContainer component={Paper} sx={{ mb: 4 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Opened</TableCell>
                <TableCell>Completed</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <SortableContext
                items={todoItems.map(item => item._id)}
                strategy={verticalListSortingStrategy}
              >
                {todoItems.map(item => (
                  <SortableItem
                    key={item._id}
                    item={item}
                    onEdit={(item) => {
                      setEditingItem(item);
                      setFormData({
                        type: item.type,
                        title: item.title,
                        description: item.description,
                        status: item.status
                      });
                      setDialogOpen(true);
                    }}
                    onDelete={handleDelete}
                  />
                ))}
              </SortableContext>
            </TableBody>
          </Table>
        </TableContainer>
      </DndContext>

      <Typography variant="h5" gutterBottom>
        Known Bugs
      </Typography>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Opened</TableCell>
                <TableCell>Completed</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <SortableContext
                items={bugItems.map(item => item._id)}
                strategy={verticalListSortingStrategy}
              >
                {bugItems.map(item => (
                  <SortableItem
                    key={item._id}
                    item={item}
                    onEdit={(item) => {
                      setEditingItem(item);
                      setFormData({
                        type: item.type,
                        title: item.title,
                        description: item.description,
                        status: item.status
                      });
                      setDialogOpen(true);
                    }}
                    onDelete={handleDelete}
                  />
                ))}
              </SortableContext>
            </TableBody>
          </Table>
        </TableContainer>
      </DndContext>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{editingItem ? 'Edit Item' : 'Add New Item'}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Type</InputLabel>
            <Select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value as 'todo' | 'bug' })}
              label="Type"
            >
              <MenuItem value="todo">Todo</MenuItem>
              <MenuItem value="bug">Bug</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            margin="normal"
            label="Title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            multiline
            rows={3}
          />

          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value as 'open' | 'in_progress' | 'completed' })}
              label="Status"
            >
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editingItem ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoadmapEdit; 